import axios from 'axios';

// Use relative URL so it works from any domain (custom domain, preview, etc.)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// In production (custom domain), always use relative path '/api'
// REACT_APP_BACKEND_URL is only needed for preview environment
const isPreview = typeof window !== 'undefined' && 
  window.location.hostname.includes('preview.emergentagent.com');

const API = (isPreview && BACKEND_URL) ? `${BACKEND_URL}/api` : '/api';

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

// Token management
const getAccessToken = () => localStorage.getItem('trafficos_token');
const getRefreshToken = () => localStorage.getItem('trafficos_refresh_token');
const setTokens = (access, refresh) => {
  localStorage.setItem('trafficos_token', access);
  if (refresh) localStorage.setItem('trafficos_refresh_token', refresh);
};
const clearTokens = () => {
  localStorage.removeItem('trafficos_token');
  localStorage.removeItem('trafficos_refresh_token');
  localStorage.removeItem('trafficos_email');
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 with token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh')) {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${API}/auth/refresh`, {
          refresh_token: refreshToken,
        });
        setTokens(data.access_token, data.refresh_token);
        processQueue(null, data.access_token);
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  refresh: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
  logout: () => {
    const refreshToken = getRefreshToken();
    return api.post('/auth/logout', { refresh_token: refreshToken }).finally(() => {
      clearTokens();
    });
  },
  me: () => api.get('/auth/me'),
};

// Dashboard
export const dashboardAPI = {
  stats: () => api.get('/dashboard/stats'),
};

// Upload
export const uploadAPI = {
  logo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLogo: (filename) => api.delete(`/upload/logo/${filename}`),
};

// Sites
export const sitesAPI = {
  list: (params) => api.get('/sites', { params }),
  get: (id) => api.get(`/sites/${id}`),
  create: (data) => api.post('/sites', data),
  update: (id, data) => api.put(`/sites/${id}`, data),
  delete: (id) => api.delete(`/sites/${id}`),
  clone: (id) => api.post(`/sites/${id}/clone`),
  updateStatus: (id, status) => api.put(`/sites/${id}/status`, { status }),
  updateAffiliate: (id, data) => api.put(`/sites/${id}/affiliate`, data),
  updateRedirect: (id, data) => api.put(`/sites/${id}/redirect`, data),
  seedPages: (id) => api.post(`/sites/${id}/seed-pages`),
  bulkCreate: (sites) => api.post('/sites/bulk', { sites }),
  bulkCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/sites/bulk-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Pages
export const pagesAPI = {
  list: (siteId) => api.get(`/sites/${siteId}/pages`),
  get: (siteId, pageId) => api.get(`/sites/${siteId}/pages/${pageId}`),
  update: (siteId, pageId, data) => api.put(`/sites/${siteId}/pages/${pageId}`, data),
  publish: (siteId, pageId) => api.put(`/sites/${siteId}/pages/${pageId}/publish`),
};

// AI Generation
export const aiAPI = {
  generate: (siteId, type) => api.post(`/sites/${siteId}/generate`, { type }),
  status: (siteId) => api.get(`/sites/${siteId}/generation-status`),
  health: () => api.get('/ai/health'),
};

// Preview
export const previewAPI = {
  getData: (siteId, slug) => api.get(`/sites/${siteId}/preview-data`, { params: { slug } }),
  getHTML: (siteId, slug) => {
    const token = getAccessToken();
    return `${API}/sites/${siteId}/preview?slug=${encodeURIComponent(slug || '/')}&token=${token || ''}`;
  },
};

// Export
export const exportAPI = {
  download: (siteId) => {
    const token = getAccessToken();
    return `${API}/sites/${siteId}/export?token=${token || ''}`;
  },
};

// SEO
export const seoAPI = {
  audit: (siteId) => api.get(`/sites/${siteId}/seo/audit`),
  sitemap: (siteId) => {
    const token = getAccessToken();
    return `${API}/sites/${siteId}/seo/sitemap?token=${token || ''}`;
  },
  robots: (siteId) => {
    const token = getAccessToken();
    return `${API}/sites/${siteId}/seo/robots?token=${token || ''}`;
  },
  linkGraph: (siteId) => api.get(`/sites/${siteId}/seo/link-graph`),
};

// Events
export const eventsAPI = {
  track: (data) => api.post('/events/click', data),
  list: (siteId) => api.get(`/sites/${siteId}/events`),
};

// Seed
export const seedAPI = {
  create: () => api.post('/seed'),
};

export { setTokens, clearTokens, getAccessToken };
export default api;
